/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { CSSProperties, ReactNode } from 'react';
import Text from '@/shared/ui/components/basic/Text';
import { cn } from '@/shared/ui/utils/tailwind';

interface AccentPanelProps {
  /** The bar's colour: a token reference, not a literal. */
  accent: string;
  children: ReactNode;
  /** Overrides the panel's own surface — warnings tint theirs. */
  surface?: string;
  /** Extra classes for the shell: padding, spacing, text size. */
  className?: string;
  /** Extra inline style for the shell, for callers still on style props. */
  style?: CSSProperties;
  testId?: string;
}

/**
 * A panel whose colour bar sits inside the card, not on its edge.
 *
 * The older form painted the accent as `border-left` on the container. A
 * border is part of the box: it meets the corner radius, so the bar bends
 * around it, and it replaces that side of the card's own outline, so the
 * panel loses its left edge and reads as bleeding off the surface it sits
 * on. Making the bar a flex child instead keeps the card's four borders
 * intact and lets the bar be a rounded, inset rule.
 *
 * Matches `OverviewTile`, where the pattern already worked, so the two
 * cannot drift apart again.
 *
 * @param props - Component props.
 * @returns The panel.
 */
export function AccentPanel({ accent, children, surface, className, style, testId }: AccentPanelProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex items-stretch gap-(--space-3) rounded-(--radius-lg)',
        'border border-(--border-subtle) px-(--space-3-5) py-(--space-3) min-w-0',
        className,
      )}
      style={{ background: surface ?? 'var(--surface-raised)', ...style }}
    >
      <Text
        component="span"
        aria-hidden="true"
        className="w-[3px] flex-none rounded-(--radius-full)"
        style={{ background: accent }}
      />
      <div className="flex flex-1 flex-col gap-(--space-2) min-w-0">
        {children}
      </div>
    </div>
  );
}

export default AccentPanel;
